import { db } from './firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';

/**
 * Broker Notification Service
 * จัดการการสร้างและอัพเดท broker notifications
 */

class BrokerNotificationService {
  constructor() {
    this.collectionName = 'tbl_broker_notifications';
  }

  /**
   * สร้าง broker notification ใหม่
   */
  async createNotification(params) {
    try {
      const {
        userId,
        broker,
        type,
        message,
        log,
        accountId,
        accountName,
        sessionId,
        status = 'success',
        metadata = {}
      } = params;

      const now = new Date();
      
      const notification = {
        userId,
        broker,
        type,
        message,
        log,
        accountId,
        accountName,
        sessionId,
        status,
        metadata,
        timestamp: serverTimestamp(),
        createdAt: now,
        updatedAt: now
      };

      // เพิ่ม loginAt สำหรับ login notifications
      if (type === 'login') {
        notification.loginAt = now;
      }

      console.log('Creating broker notification:', {
        broker,
        type,
        message,
        sessionId
      });

      const docRef = await addDoc(collection(db, this.collectionName), notification);
      
      console.log('✅ Broker notification created successfully:', docRef.id);
      
      return {
        success: true,
        notification: {
          id: docRef.id,
          ...notification
        }
      };

    } catch (error) {
      console.error('❌ Error creating broker notification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * สร้าง login notification
   */
  async createLoginNotification(userId, broker, accountInfo) {
    const sessionId = `session_${Date.now()}`;
    
    return this.createNotification({
      userId,
      broker,
      type: 'login',
      message: `${broker.charAt(0).toUpperCase() + broker.slice(1)} connection established`,
      log: `${broker.charAt(0).toUpperCase() + broker.slice(1)} - Connection established`,
      accountId: accountInfo.accountId || accountInfo.account_number,
      accountName: accountInfo.accountName || accountInfo.name,
      sessionId,
      status: 'active'
    });
  }

  /**
   * อัพเดท notification เมื่อ logout
   */
  async updateLogoutNotification(sessionId, logoutAt) {
    try {
      console.log('🔄 Updating logout notification for session:', sessionId);

      // หา notification ที่มี sessionId นี้และยัง active อยู่
      const q = query(
        collection(db, this.collectionName),
        where('sessionId', '==', sessionId),
        where('status', '==', 'active')
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.warn('⚠️ No active session found for sessionId:', sessionId);
        return { success: false, error: 'No active session found' };
      }

      const docRef = snapshot.docs[0].ref;
      const docData = snapshot.docs[0].data();
      
      // คำนวณ duration
      const loginAt = docData.loginAt?.toDate() || new Date(docData.createdAt);
      const duration = Math.floor((logoutAt - loginAt) / 1000); // วินาที
      
      // Format duration
      const durationText = this.formatDuration(duration);

      await updateDoc(docRef, {
        type: 'logout',
        message: 'Session ended',
        log: `${docData.broker} - Session ended (Duration: ${durationText})`,
        logoutAt: logoutAt,
        duration: duration,
        status: 'completed',
        updatedAt: new Date()
      });

      console.log('✅ Logout notification updated successfully');
      
      return { success: true };

    } catch (error) {
      console.error('❌ Error updating logout notification:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * สร้าง error notification
   */
  async createErrorNotification(userId, broker, errorType, errorMessage, accountInfo = {}) {
    return this.createNotification({
      userId,
      broker,
      type: errorType,
      message: `Error: ${errorMessage}`,
      log: `${broker} - ${errorMessage}`,
      accountId: accountInfo.accountId || accountInfo.account_number,
      accountName: accountInfo.accountName || accountInfo.name,
      status: 'failed',
      metadata: {
        errorMessage: errorMessage
      }
    });
  }

  /**
   * ดึง notifications ของ user
   */
  async getUserNotifications(userId, limit = 50) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limit)
      );

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

    } catch (error) {
      console.error('❌ Error fetching user notifications:', error);
      return [];
    }
  }

  /**
   * Format duration เป็นข้อความที่อ่านง่าย
   */
  formatDuration(seconds) {
    if (seconds < 60) {
      return `${seconds} seconds`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  }
}

// Export singleton instance
const brokerNotificationService = new BrokerNotificationService();
export default brokerNotificationService;
